import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, X, ArrowUp, ArrowDown, Pencil, RotateCcw } from 'lucide-react';
import { sans, useTheme } from '../../tokens';
import {
  getTextOverride, setTextOverride, clearTextOverride, subscribeToTextOverrideChanges,
} from '../../data/textOverridesStore';

function formatRefreshed(ts) {
  if (!ts) return null;
  const d = ts instanceof Date ? ts : new Date(ts);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function CollapsibleSection({ id, title, subtitle, icon: Icon, children, defaultOpen = true, summaryWhenClosed, forceOpen, lastRefreshed, onHide, onMoveUp, onMoveDown, canMoveUp, canMoveDown, editable, companyId, selectedPeriod }) {
  const titleKey = `section-title:${id}`;
  // Always read the override (so investors see it too), but only the
  // `editable` flag controls whether the admin pencil appears.
  const [override, setOverride] = useState(() => getTextOverride(companyId, selectedPeriod, titleKey));
  useEffect(() => {
    setOverride(getTextOverride(companyId, selectedPeriod, titleKey));
    return subscribeToTextOverrideChanges(() => setOverride(getTextOverride(companyId, selectedPeriod, titleKey)));
  }, [companyId, selectedPeriod, titleKey]);
  const displayTitle = override !== undefined ? override : title;
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const startTitleEdit = () => { setTitleDraft(displayTitle || ''); setEditingTitle(true); };
  const saveTitleEdit = () => {
    const v = titleDraft.trim();
    if (v && v !== title) setTextOverride(companyId, selectedPeriod, titleKey, v);
    else if (v === title || !v) clearTextOverride(companyId, selectedPeriod, titleKey);
    setEditingTitle(false);
  };
  const resetTitle = () => { clearTextOverride(companyId, selectedPeriod, titleKey); };
  return _renderSection({
    id, title: displayTitle, subtitle, Icon, children, defaultOpen, summaryWhenClosed, forceOpen, lastRefreshed,
    onHide, onMoveUp, onMoveDown, canMoveUp, canMoveDown,
    editable, editingTitle, titleDraft, setTitleDraft, startTitleEdit, saveTitleEdit,
    hasOverride: override !== undefined, resetTitle, cancelTitleEdit: () => setEditingTitle(false),
  });
}

function _renderSection({ id, title, subtitle, Icon, children, defaultOpen, summaryWhenClosed, forceOpen, lastRefreshed, onHide, onMoveUp, onMoveDown, canMoveUp, canMoveDown, editable, editingTitle, titleDraft, setTitleDraft, startTitleEdit, saveTitleEdit, hasOverride, resetTitle, cancelTitleEdit }) {
  const t = useTheme();
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  // Initialize from localStorage if available, else use defaultOpen
  const [isOpen, setIsOpen] = useState(() => {
    const stored = localStorage.getItem('inv-section-' + id);
    if (stored === 'open') return true;
    if (stored === 'closed') return false;
    return defaultOpen;
  });

  // Allow parent to force open
  useEffect(() => {
    if (forceOpen && !isOpen) {
      setIsOpen(true);
      localStorage.setItem('inv-section-' + id, 'open');
    }
  }, [forceOpen]);

  // Measure content height for smooth animation
  const updateHeight = useCallback(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, []);

  useEffect(() => {
    updateHeight();
    // Re-measure on window resize
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [updateHeight, children]);

  // Also re-measure after a short delay to catch chart renders
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(updateHeight, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, updateHeight]);

  const toggle = () => {
    const next = !isOpen;
    setIsOpen(next);
    localStorage.setItem('inv-section-' + id, next ? 'open' : 'closed');
  };

  return (
    <div style={{
      background: t.bgCard,
      border: `1px solid ${t.border}`,
      borderRadius: 10,
      padding: '20px 24px',
      marginBottom: 16,
    }}>
      {/* Title area */}
      <div
        onClick={toggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        {Icon && <Icon size={16} style={{ color: t.textMuted, flexShrink: 0 }} />}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
          {editingTitle ? (
            <input
              value={titleDraft}
              autoFocus
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setTitleDraft(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') saveTitleEdit();
                if (e.key === 'Escape') cancelTitleEdit();
              }}
              onBlur={saveTitleEdit}
              style={{
                fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
                color: t.textPrimary, fontFamily: sans,
                background: t.bgSurface, border: `1px solid ${t.border}`, borderRadius: 4,
                padding: '4px 8px', outline: 'none', minWidth: 200,
              }}
            />
          ) : (
            <div style={{
              fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
              color: t.textMuted, fontFamily: sans,
            }}>
              {title}
            </div>
          )}
          {editable && !editingTitle && (
            <button
              onClick={(e) => { e.stopPropagation(); startTitleEdit(); }}
              title="Rename section"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: t.textFaint, padding: 2, opacity: 0.6 }}
            >
              <Pencil size={11} />
            </button>
          )}
          {editable && hasOverride && !editingTitle && (
            <button
              onClick={(e) => { e.stopPropagation(); resetTitle(); }}
              title="Reset to default name"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: t.textFaint, padding: 2, opacity: 0.6 }}
            >
              <RotateCcw size={11} />
            </button>
          )}
          {subtitle && !editingTitle && (
            <div style={{ fontSize: 10, color: t.textFaint, fontFamily: sans, marginLeft: 8 }}>
              {subtitle}
            </div>
          )}
        </div>
        {/* Last refreshed is admin-only */}
        {lastRefreshed && editable && (
          <span style={{ fontSize: 9, color: t.textFaint, fontFamily: sans, marginRight: 8 }}>
            Last refreshed: {formatRefreshed(lastRefreshed)}
          </span>
        )}
        {onMoveUp && (
          <button onClick={(e) => { e.stopPropagation(); onMoveUp(); }} disabled={!canMoveUp}
            title="Move section up"
            style={{ background: 'transparent', border: 'none', cursor: canMoveUp ? 'pointer' : 'not-allowed', opacity: canMoveUp ? 0.6 : 0.2, color: t.textMuted, padding: 4, marginRight: 2 }}>
            <ArrowUp size={12} />
          </button>
        )}
        {onMoveDown && (
          <button onClick={(e) => { e.stopPropagation(); onMoveDown(); }} disabled={!canMoveDown}
            title="Move section down"
            style={{ background: 'transparent', border: 'none', cursor: canMoveDown ? 'pointer' : 'not-allowed', opacity: canMoveDown ? 0.6 : 0.2, color: t.textMuted, padding: 4, marginRight: 2 }}>
            <ArrowDown size={12} />
          </button>
        )}
        {onHide && (
          <button onClick={(e) => { e.stopPropagation(); onHide(); }}
            title="Hide this section for this month"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: t.textMuted, padding: 4, marginRight: 2, opacity: 0.6 }}>
            <X size={14} />
          </button>
        )}
        {/* Summary when closed */}
        {!isOpen && summaryWhenClosed && (
          <span style={{
            fontSize: 10,
            color: t.textFaint,
            fontFamily: sans,
            marginRight: 4,
          }}>
            {summaryWhenClosed}
          </span>
        )}
        <ChevronDown
          size={16}
          style={{
            color: t.textMuted,
            flexShrink: 0,
            transition: 'transform 0.25s ease-in-out',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </div>

      {/* Content */}
      <div style={{
        overflow: isOpen ? 'visible' : 'hidden',
        maxHeight: isOpen ? 'none' : 0,
        transition: isOpen ? 'none' : 'max-height 0.2s ease-in-out',
      }}>
        <div ref={contentRef} style={{ paddingTop: isOpen ? 14 : 0 }}>
          {children}
        </div>
      </div>
    </div>
  );
}
